import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "card", "searchInput", "trigger", "categoryButton", "outsideCardsContainer", "matchCombatantBtn"]

  connect() {
    this.categoryCache = {}
    this._matchCombatant = true
    this._onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    document.addEventListener(
      "character-picker:characterSelected",
      this.handleCharacterSelected.bind(this)
    )
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener(
      "character-picker:characterSelected",
      this.handleCharacterSelected.bind(this)
    )
  }

  onKeydown(e) {
    if (e.key !== "Escape") return
    if (this.modalTarget.classList.contains("hidden")) return

    const searchEl = this.element.querySelector("[data-controller='card-search']")
    if (searchEl) {
      const searchController = this.application.getControllerForElementAndIdentifier(searchEl, "card-search")
      if (searchController?.enabled) return // let search handle this Esc
    }

    this.close()
  }

  handleCharacterSelected(event) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.remove("hidden")
    }
  }

  // ----- Open/close -----

  open() {
    this.modalTarget.classList.remove("hidden")
    this.activateDefaultFilter()
  }

  close() {
    this.modalTarget.classList.add("hidden")

    const searchEl = this.element.querySelector("[data-controller='card-search']")
    if (searchEl) {
      const searchController = this.application.getControllerForElementAndIdentifier(searchEl, "card-search")
      if (searchController && searchController.enabled) {
        searchController.reset()
      }
    }
  }

  closeIfBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  activateDefaultFilter() {
    const seasonButton = this.element.querySelector(
      `[data-filter-group="category"][data-filter-value="season"]`
    )
    if (seasonButton) {
      seasonButton.click()
    }
  }

  // ----- Filtering -----

  filterCost(event) {
    this.toggleFilter("cost", event.currentTarget)
  }

  async filterCategory(event) {
    const button = event.currentTarget
    const category = button.dataset.filterValue
    this.toggleFilter("category", button, { skipApply: true })

    if (category === "all") {
      const categories = ["season", "common", "monster"]
      await Promise.all(categories.map(async cat => {
        if (this.categoryCache[cat]) return
        const response = await fetch(`/save_decks/picker_cards?category=${cat}`)
        const html = await response.text()
        this.categoryCache[cat] = html
      }))
      this.outsideCardsContainerTarget.innerHTML = categories.map(c => this.categoryCache[c]).join("")
      this.applyFilters()
      return
    }

    if (category === "combatant") {
      this.outsideCardsContainerTarget.innerHTML = ""
      this.applyFilters()
      return
    }

    if (this.categoryCache[category]) {
      this.outsideCardsContainerTarget.innerHTML = this.categoryCache[category]
      this.applyFilters()
      return
    }

    const response = await fetch(`/save_decks/picker_cards?category=${category}`)
    const html = await response.text()
    this.categoryCache[category] = html
    this.outsideCardsContainerTarget.innerHTML = html
    this.applyFilters()
  }

  async search() {
    const categories = ["season", "common", "monster"]

    await Promise.all(categories.map(async (category) => {
      if (this.categoryCache[category]) return
      const response = await fetch(`/save_decks/picker_cards?category=${category}`)
      const html = await response.text()
      this.categoryCache[category] = html
    }))

    this.outsideCardsContainerTarget.innerHTML = categories
      .map(c => this.categoryCache[c])
      .join("")

    // Switch active filter to All
    const allButton = this.element.querySelector(`[data-filter-group="category"][data-filter-value="all"]`)
    if (allButton) {
      this.element.querySelectorAll("[data-filter-group='category']").forEach(b => {
        b.dataset.active = "false"
        b.classList.add("brightness-50")
      })
      allButton.dataset.active = "true"
      allButton.classList.remove("brightness-50")
    }

    this.applyFilters()
  }

  toggleFilter(filterKey, button, { skipApply = false } = {}) {
    const isActive = button.dataset.active === "true"
    const groupSelector = `[data-filter-group="${filterKey}"]`

    this.element.querySelectorAll(groupSelector).forEach(b => {
      b.dataset.active = "false"
      b.classList.add("brightness-50")
    })

    // Category always activates, cost toggles off
    if (filterKey === "category" || !isActive) {
      button.dataset.active = "true"
      button.classList.remove("brightness-50")
    }

    if (!skipApply) this.applyFilters()
  }

  applyFilters() {
    const activeCost = this.activeFilterValue("cost")
    const activeCategory = this.activeFilterValue("category")
    const searchTerm = this.searchInputTarget.value.toLowerCase()

    const deckEl = document.querySelector("[data-controller='deck']")
    const characterJobs = this._matchCombatant
      ? (deckEl?.dataset.deckCharacterJobs || "").toLowerCase().split(",").filter(Boolean)
      : []

    const cards = this.element.querySelectorAll(
      "[data-card-picker-target='card']:not([data-deck-target='addedCard'])"
    )

    cards.forEach(card => {
      const matchesCost = !activeCost || this.matchesCostFilter(card.dataset.cost, activeCost)
      const cardCategory = card.dataset.cardCategory || card.dataset.category
      const matchesCategory = !activeCategory || activeCategory === "all" || cardCategory === activeCategory
      const matchesSearch = !searchTerm || card.dataset.cardName.toLowerCase().includes(searchTerm)

      const cardJobs = card.dataset.jobs ? card.dataset.jobs.split(",").filter(Boolean) : []
      const matchesJob = !this._matchCombatant ||
                        cardJobs.length === 0 ||
                        cardJobs.some(j => characterJobs.includes(j.toLowerCase()))

      const visible = matchesCost && matchesCategory && matchesSearch && matchesJob
      card.classList.toggle("hidden", !visible)
    })
  }

  matchesCostFilter(cardCost, filterValue) {
    if (filterValue === "4+") return parseInt(cardCost, 10) >= 4
    return cardCost === filterValue
  }

  activeFilterValue(filterKey) {
    const button = this.element.querySelector(`[data-filter-group="${filterKey}"][data-active="true"]`)
    return button ? button.dataset.filterValue : null
  }

  selectCard(event) {
    const card = event.currentTarget
    this.dispatch("cardAdded", {
      detail: {
        cardId: card.dataset.cardId,
        cardCategory: card.dataset.category
      }
    })
    this.close()
  }

  toggleMatchCombatant() {
    this._matchCombatant = !this._matchCombatant
    this.matchCombatantBtnTarget.classList.toggle("match-combatant-btn--active", this._matchCombatant)
    this.applyFilters()
  }
}
